import React from 'react';
import { getEditorPreloader } from '@confluence-classic/confluence-fabric-editor/src/lib/index.ts';
import { ClassicEditorContextProviderLoader } from '@confluence-classic/confluence-fabric-editor/src/components/ClassicEditorContextProvider/index.js';

import type { RouteMatch } from '@confluence/route/entry-points/Route.ts';
import { EditPageLoader } from '@confluence/full-page-editor/entry-points/EditPageLoader.ts';
import { EditPageLoaderServer } from '@confluence/full-page-editor/entry-points/EditPageLoaderServer.ts';
import { PageShownPublicLinksEventLoader } from '@confluence/external-share-utils/entry-points/PageShownPublicLinksEventLoader.tsx';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import { getUniquePageLoadId } from '@confluence/unique-page-load-id/entry-points/UniquePageLoadId.ts';
import { END } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import {
	CONTAINER_HOME_ID,
	PRODUCT_HOME_ID,
} from '@confluence/app-navigation/entry-points/navigationIDs.ts';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils.ts';
import { AnnotationsProvider } from '@confluence/annotation-provider-store/entry-points/AnnotationsContext.ts';
import { Redirection } from '@confluence/route-manager/entry-points/Redirection.ts';
import { COMPANY_HUB_EDIT } from '@confluence/named-routes/entry-points/routes.ts';
import { isSSRStreaming } from '@confluence/ssr-utilities/entry-points/isSSRStreaming.ts';
import type { IEditorPreloader } from '@confluence/edit-button/entry-points/types.ts';

type EditPageContentProps = {
	contentId: string;
	spaceKey: string;
	draftShareId?: string;
	contentType: string;
};

const EditPageContent = ({
	contentId,
	spaceKey,
	draftShareId,
	contentType,
}: EditPageContentProps) =>
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	process.env.REACT_SSR ? (
		<EditPageLoaderServer
			contentId={contentId}
			spaceKey={spaceKey}
			draftShareId={draftShareId}
			contentType={contentType}
		/>
	) : (
		<EditPageLoader
			contentId={contentId}
			spaceKey={spaceKey}
			draftShareId={draftShareId}
			contentType={contentType}
			ClassicEditorContextProviderLoader={ClassicEditorContextProviderLoader}
		/>
	);

export const EditPageOrBlogV2Route: {
	(routeMatch: RouteMatch): React.JSX.Element;
	NAVIGATION_PARAMS(routeMatch: RouteMatch): {
		Screen: {
			screenEvent: {
				name: string;
				id: string;
				attributes: {
					isPublicView: boolean;
					pageLoadInfo: {
						isLivePage?: boolean | undefined;
						ufoTraceId?: string | undefined;
						ufoTraceType?: string | undefined;
						uuid: string | null;
						contentId: string | null;
						isInitial: boolean;
						isSSR: boolean;
						routingDuration: number;
					};
					navdexPointType: string;
				};
			};
			pageState: {
				routeName: string;
				spaceKey: string;
				contentId: string;
				contentType: string;
			};
			showPremiumOnboarding: boolean;
		};
		MainLayout: {
			navView: string;
			spaceKey: string;
			editorPreloader: (() => Promise<() => (config: IEditorPreloader) => JSX.Element>) | undefined;
			disableMinWidth: boolean;
			isViewPage: boolean;
			isEdit: boolean;
		};
	};
} = ({
	params: { contentId, spaceKey, contentType: editRouteTypeParam },
	query,
	pathname,
}: RouteMatch): React.JSX.Element => {
	const draftShareId = typeof query?.draftShareId === 'string' ? query.draftShareId : undefined;

	// editRouteTypeParam !== contentType !== pageType
	// editContentType is blog or pages
	// contentType is blogpost or page
	// pageType is blog or page
	const contentType = editRouteTypeParam === 'blog' ? 'blogpost' : 'page';

	const isCompanyHub = isCompanyHubSpaceKey(spaceKey);
	const shouldRedirectCompanyHubEditRoute = isCompanyHub && COMPANY_HUB_EDIT.pattern !== pathname;
	if (shouldRedirectCompanyHubEditRoute) {
		// The company hub edit route should handle upsell scenarios and other cases
		return <Redirection name={COMPANY_HUB_EDIT.name} />;
	}

	return (
		<AnnotationsProvider>
			<>
				{isSSRStreaming() ? (
					<EditPageContent
						contentId={contentId}
						spaceKey={spaceKey}
						draftShareId={draftShareId}
						contentType={contentType}
					/>
				) : (
					<>
						<EditPageLoaderServer
							contentId={contentId}
							spaceKey={spaceKey}
							draftShareId={draftShareId}
							contentType={contentType}
						/>
						<EditPageLoader
							contentId={contentId}
							spaceKey={spaceKey}
							draftShareId={draftShareId}
							contentType={contentType}
							ClassicEditorContextProviderLoader={ClassicEditorContextProviderLoader}
						/>
					</>
				)}
				<PageShownPublicLinksEventLoader
					contentId={contentId}
					isPage={editRouteTypeParam === 'page'}
					isEditPage
				/>
			</>
		</AnnotationsProvider>
	);
};

EditPageOrBlogV2Route.NAVIGATION_PARAMS = ({
	name: routeName,
	params: { contentId, spaceKey, contentType: editRouteTypeParam },
}: RouteMatch): {
	Screen: {
		screenEvent: {
			name: string;
			id: string;
			attributes: {
				isPublicView: boolean;
				pageLoadInfo: {
					isLivePage?: boolean | undefined;
					ufoTraceId?: string | undefined;
					ufoTraceType?: string | undefined;
					uuid: string | null;
					contentId: string | null;
					isInitial: boolean;
					isSSR: boolean;
					routingDuration: number;
				};
				navdexPointType: string;
			};
		};
		pageState: {
			routeName: string;
			spaceKey: string;
			contentId: string;
			contentType: string;
		};
		showPremiumOnboarding: boolean;
	};
	MainLayout: {
		navView: string;
		spaceKey: string;
		editorPreloader: (() => Promise<() => (config: IEditorPreloader) => JSX.Element>) | undefined;
		disableMinWidth: boolean;
		isViewPage: boolean;
		isEdit: boolean;
	};
} => {
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const isEmbeddedConfluence = useIsEmbeddedConfluence_DO_NOT_USE();
	const showPremiumOnboarding = !isEmbeddedConfluence;
	const isPageLikeContent = editRouteTypeParam === 'pages' || editRouteTypeParam === 'blog';
	const isBlog = editRouteTypeParam === 'blog';

	// editRouteTypeParam !== contentType
	// editRouteTypeParam is blog or pages
	// contentType is blogpost or page
	const contentType = editRouteTypeParam === 'blog' ? 'blogpost' : 'page';

	let viewId = CONTAINER_HOME_ID;
	if (isCompanyHubSpaceKey(spaceKey)) {
		viewId = PRODUCT_HOME_ID;
	} else if (isBlog) {
		viewId = CONTAINER_HOME_ID;
	}

	return {
		Screen: {
			screenEvent: {
				name: 'editPageScreen',
				id: contentId,
				attributes: {
					isPublicView: false,
					pageLoadInfo: getUniquePageLoadId(),
					navdexPointType: END,
				},
			},
			pageState: {
				routeName,
				spaceKey,
				contentId,
				contentType,
			},
			showPremiumOnboarding: isPageLikeContent ? showPremiumOnboarding : false,
		},
		MainLayout: {
			navView: viewId,
			spaceKey,
			editorPreloader: isPageLikeContent ? getEditorPreloader : undefined,
			disableMinWidth: false,
			isViewPage: true,
			isEdit: true,
		},
	};
};
